import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search,
  ChevronDown,
  MapPin,
  Users,
  Trophy,
  Feather,
  BadgeCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { API, useAuth } from "@/App";
import { resolveAssetUrl } from "@/lib/assetUrl";

// ── Design tokens ───────────────────────────────────────────────────────
// Colours + type match the reference mockup exactly:
//   • Cream background        → bg-tbc-cream
//   • Navy text + accents     → text-tbc-navy / bg-tbc-navy
//   • Muted gold              → text-tbc-gold / bg-tbc-gold
//   • Serif headline          → font-heading (Playfair)
//   • Ledger table rows are separated by a hairline gold line to feel
//     institutional, not SaaS.

// Category filter list. Populated dynamically from the ledger so we only
// show categories that actually have founders.
const ALL_LABEL = "All Categories";
const ALL_STATE_LABEL = "All Locations";

// Two-letter avatar bubble used in the ledger when a founder has no logo
// (mockup-accurate style). Deterministic colour per business_id so the
// table doesn't feel randomised on refresh.
function EntryAvatar({ entry }) {
  const url = entry.logo_url ? resolveAssetUrl(entry.logo_url) : null;
  const initials = (entry.name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => (w[0] || "").toUpperCase())
    .join("");
  return (
    <div className="flex-none h-9 w-9 rounded-full bg-tbc-navy ring-1 ring-tbc-gold/40 overflow-hidden flex items-center justify-center">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="font-heading text-[11px] text-tbc-gold">{initials}</span>
      )}
    </div>
  );
}

// Prestige donut chart. Renders the claimed vs. remaining ratio using a
// single conic-gradient for a crisp gold arc. Pure CSS — no chart lib.
function ClaimedDonut({ percent }) {
  const angle = Math.max(0, Math.min(100, percent)) * 3.6;
  return (
    <div className="relative h-40 w-40 sm:h-44 sm:w-44 flex-none">
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: `conic-gradient(#C8A44A 0deg ${angle}deg, #E0D8C5 ${angle}deg 360deg)`,
        }}
      />
      <div className="absolute inset-2 rounded-full bg-tbc-cream" />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-heading text-3xl sm:text-4xl text-tbc-navy leading-none">
          {percent}%
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-tbc-navy/70">
          Claimed
        </div>
      </div>
    </div>
  );
}

// Stats panel — the big framed number card next to the headline.
function StatsPanel({ claimed, remaining, percent, closed }) {
  return (
    <div className="relative rounded-2xl bg-white/95 p-6 sm:p-8 shadow-[0_1px_0_rgba(200,164,74,0.35),0_18px_44px_-24px_rgba(13,27,52,0.35)] ring-1 ring-tbc-gold/30">
      <div className="grid grid-cols-3 items-center gap-3 sm:gap-6">
        <div className="text-center">
          <div className="font-heading text-4xl sm:text-6xl text-tbc-navy leading-none" data-testid="founding-claimed-count">
            {claimed}
          </div>
          <div className="mt-2 text-[10px] sm:text-xs uppercase tracking-[0.16em] font-semibold text-tbc-navy/70 leading-tight">
            Founding
            <br className="sm:hidden" />
            {" "}
            Businesses
            <br />
            Claimed
          </div>
          <Users className="mx-auto mt-3 h-5 w-5 text-tbc-gold" strokeWidth={1.5} />
        </div>
        <div className="flex justify-center">
          <ClaimedDonut percent={percent} />
        </div>
        <div className="text-center">
          <div className="font-heading text-4xl sm:text-6xl text-tbc-navy leading-none" data-testid="founding-remaining-count">
            {remaining}
          </div>
          <div className="mt-2 text-[10px] sm:text-xs uppercase tracking-[0.16em] font-semibold text-tbc-navy/70 leading-tight">
            Positions
            <br />
            Remaining
          </div>
          <Trophy className="mx-auto mt-3 h-5 w-5 text-tbc-gold" strokeWidth={1.5} />
        </div>
      </div>
      <div className="mt-6 flex justify-center">
        <div className="h-px w-16 bg-tbc-gold/50" />
      </div>
      <p className="mt-4 text-center font-body text-xs sm:text-sm text-tbc-navy/80 flex items-center justify-center gap-2">
        <Trophy className="h-3.5 w-3.5 text-tbc-gold flex-none" />
        {closed
          ? "The Founding Charter is complete."
          : "Only 100 Founding Business positions will ever exist."}
      </p>
    </div>
  );
}

// TBC circular seal — sits opposite the headline.
function TBCSeal() {
  return (
    <div
      className="flex-none h-32 w-32 sm:h-40 sm:w-40 rounded-full bg-tbc-navy border-2 border-tbc-gold flex flex-col items-center justify-center text-tbc-gold shadow-lg"
      aria-hidden="true"
      data-testid="founding-tbc-seal"
    >
      <div className="font-heading text-3xl sm:text-4xl leading-none">TBC</div>
      <div className="mt-1 text-[9px] tracking-[0.3em] font-semibold">EST. 2024</div>
    </div>
  );
}

// Simple styled <select> with the muted-gold caret to match the mockup.
function SoftSelect({ value, onChange, options, testId }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full appearance-none rounded-xl border border-tbc-navy/15 bg-white/95 px-4 py-3 pr-9 font-body text-sm text-tbc-navy focus:outline-none focus:ring-2 focus:ring-tbc-gold/50"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tbc-navy/50" />
    </div>
  );
}

// The ledger table. Uses a responsive layout: full grid on ≥md, stacked
// cards on mobile so no horizontal scroll or squeezed columns.
function LedgerRow({ entry }) {
  const location = [entry.city, entry.state].filter(Boolean).join(", ") || "—";
  const category = entry.category || "—";
  const src = (entry.founder_source || "legacy").toLowerCase();
  const sourceLabel = src === "stripe" ? "Stripe"
    : src === "admin" ? "Admin"
    : src === "instagram_sync" ? "IG"
    : "Legacy";
  const active = entry.is_active !== false;

  return (
    <div
      id={String(entry.founder_number).padStart(3, "0")}
      data-testid={`founding-entry-${entry.founder_number}`}
      className="grid grid-cols-1 md:grid-cols-[64px_minmax(0,1.6fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_88px_86px_60px] items-center gap-3 md:gap-4 px-4 md:px-6 py-4 border-b border-tbc-gold/15 hover:bg-tbc-cream/40 transition-colors target:bg-tbc-gold/10"
    >
      <div className="font-heading text-tbc-gold text-lg md:text-base tabular-nums" data-testid={`founding-number-${entry.founder_number}`}>
        {String(entry.founder_number).padStart(3, "0")}
      </div>
      <div className="flex items-center gap-3 min-w-0">
        <EntryAvatar entry={entry} />
        <div className="min-w-0">
          <div className="font-heading text-tbc-navy text-[15px] truncate flex items-center gap-1.5">
            {entry.name}
            {entry.is_verified && (
              <BadgeCheck className="h-3.5 w-3.5 text-tbc-gold flex-none" />
            )}
          </div>
          <div className="md:hidden text-xs text-tbc-navy/60 mt-0.5">
            {category} · {location}
          </div>
        </div>
      </div>
      <div className="hidden md:block font-body text-sm text-tbc-navy/80 truncate">{category}</div>
      <div className="hidden md:flex items-center gap-1.5 font-body text-sm text-tbc-navy/80 min-w-0">
        <MapPin className="h-3.5 w-3.5 text-tbc-gold flex-none" />
        <span className="truncate">{location}</span>
      </div>
      <div className="hidden md:flex items-center gap-1.5">
        <span className="h-6 w-6 rounded-full bg-tbc-gold/20 border border-tbc-gold/50 flex items-center justify-center text-[10px] font-heading text-tbc-gold">100</span>
        <span className="text-xs font-body text-tbc-navy/70">{sourceLabel}</span>
      </div>
      <div className="hidden md:block">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ${
            active
              ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
              : "bg-gray-100 text-gray-600 ring-1 ring-gray-200"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-gray-400"}`}
          />
          {active ? "Active" : "Historic"}
        </span>
      </div>
      <div className="hidden md:block text-right">
        <Link
          to={`/business/${entry.business_id}`}
          className="text-tbc-navy font-body text-sm hover:text-tbc-gold"
          data-testid={`founding-view-${entry.founder_number}`}
        >
          View →
        </Link>
      </div>
    </div>
  );
}

export default function FoundingCharterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hash } = useLocation();

  const [ledger, setLedger] = useState({
    total: 100,
    claimed: 0,
    remaining: 100,
    percent: 0,
    closed: false,
    entries: [],
  });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(ALL_LABEL);
  const [loc, setLoc] = useState(ALL_STATE_LABEL);
  const [sort, setSort] = useState("Sort by Entry No.");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/founder/public/ledger`);
        if (!cancelled) setLedger(r.data);
      } catch {
        if (!cancelled) setLedger((l) => ({ ...l }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Scroll to a specific founder entry when the URL carries a hash like
  // `#027`. Small delay so the ledger has painted its rows first.
  useEffect(() => {
    if (!hash || loading) return;
    const target = hash.replace(/^#/, "");
    if (!target) return;
    const t = setTimeout(() => {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(t);
  }, [hash, loading]);

  // Filter + sort the ledger client-side. The list is capped at 100 so
  // there's no need for server-side pagination — one round trip is fine.
  const categories = useMemo(() => {
    const s = new Set(ledger.entries.map((e) => e.category).filter(Boolean));
    return [ALL_LABEL, ...Array.from(s).sort()];
  }, [ledger.entries]);
  const locations = useMemo(() => {
    const s = new Set(
      ledger.entries.map((e) => [e.city, e.state].filter(Boolean).join(", ")).filter(Boolean),
    );
    return [ALL_STATE_LABEL, ...Array.from(s).sort()];
  }, [ledger.entries]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = ledger.entries.filter((e) => {
      if (cat !== ALL_LABEL && (e.category || "") !== cat) return false;
      if (loc !== ALL_STATE_LABEL) {
        const l = [e.city, e.state].filter(Boolean).join(", ");
        if (l !== loc) return false;
      }
      if (needle) {
        const hay = [
          e.name,
          e.category,
          e.city,
          e.state,
          String(e.founder_number || ""),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    if (sort === "Sort by Name") {
      rows = [...rows].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else {
      rows = [...rows].sort(
        (a, b) => (a.founder_number || 0) - (b.founder_number || 0),
      );
    }
    return rows;
  }, [ledger.entries, q, cat, loc, sort]);

  const isSignedIn = !!user;
  const canClaim = !ledger.closed;

  const primaryCTA = canClaim ? (
    <Link
      to={isSignedIn ? "/pricing#founding" : "/pricing#founding"}
      className="inline-flex items-center gap-2 rounded-md bg-tbc-navy px-6 py-3 text-white font-body font-semibold hover:bg-tbc-navy/90 transition-colors shadow-md"
      data-testid="founding-claim-cta"
    >
      Claim Your Entry <span aria-hidden>→</span>
    </Link>
  ) : (
    <div
      className="inline-flex items-center gap-2 rounded-md bg-tbc-navy/40 px-6 py-3 text-white font-body font-semibold cursor-not-allowed"
      data-testid="founding-closed-badge"
    >
      Charter Closed
    </div>
  );

  return (
    <div className="min-h-screen bg-tbc-cream">
      <Navbar />

      {/* ================ HEADLINE + STATS ================ */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 flex items-center gap-5 sm:gap-8">
            <TBCSeal />
            <div className="min-w-0">
              <div className="text-[11px] sm:text-xs uppercase tracking-[0.28em] font-semibold text-tbc-navy/60">
                The <span className="text-tbc-gold">Founding Charter</span>
              </div>
              <h1
                className="font-heading text-tbc-navy mt-2 leading-[1.02]"
                style={{ fontSize: "clamp(2rem, 5.5vw, 3.75rem)" }}
                data-testid="founding-headline"
              >
                One hundred names,
                <br />
                <span className="italic text-tbc-gold">written once.</span>
              </h1>
              <p className="mt-4 max-w-[42ch] font-body text-tbc-navy/80 leading-relaxed text-sm sm:text-base">
                Every business that joins the Founding 100 is entered into the
                permanent record of TheBlkCommunity — a charter that closes the
                moment the hundredth name is signed.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                {primaryCTA}
                <a
                  href="#ledger"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("ledger")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="inline-flex items-center gap-2 rounded-md bg-tbc-navy px-6 py-3 text-white font-body font-semibold hover:bg-tbc-navy/90 transition-colors shadow-md"
                  data-testid="founding-read-ledger"
                >
                  Read the Ledger
                </a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <StatsPanel
              claimed={ledger.claimed}
              remaining={ledger.remaining}
              percent={ledger.percent}
              closed={ledger.closed}
            />
          </div>
        </div>
      </section>

      {/* ================ SEARCH + FILTERS ================ */}
      <section id="ledger" className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="rounded-2xl bg-white/95 ring-1 ring-tbc-gold/20 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 p-4 md:p-5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tbc-navy/40" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by business name, owner or founder number…"
                data-testid="founding-search-input"
                className="w-full rounded-xl border border-tbc-navy/15 bg-white pl-9 pr-3 py-3 font-body text-sm text-tbc-navy placeholder:text-tbc-navy/40 focus:outline-none focus:ring-2 focus:ring-tbc-gold/50"
              />
            </div>
            <SoftSelect value={cat} onChange={setCat} options={categories} testId="founding-filter-category" />
            <SoftSelect value={loc} onChange={setLoc} options={locations} testId="founding-filter-location" />
            <SoftSelect
              value={sort}
              onChange={setSort}
              options={["Sort by Entry No.", "Sort by Name"]}
              testId="founding-sort"
            />
          </div>

          {/* Ledger header */}
          <div className="hidden md:grid grid-cols-[64px_minmax(0,1.6fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_88px_86px_60px] gap-4 px-6 py-3 border-t border-tbc-gold/20 border-b border-tbc-gold/20 bg-tbc-cream/40 text-[10px] uppercase tracking-[0.16em] font-semibold text-tbc-navy/60">
            <div>No.</div>
            <div>Business</div>
            <div>Category</div>
            <div>Location</div>
            <div>Founder Source</div>
            <div>Status</div>
            <div className="text-right">View</div>
          </div>

          {/* Ledger rows */}
          {loading ? (
            <div className="p-10 text-center font-body text-sm text-tbc-navy/60">
              Loading the ledger…
            </div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center">
              <Feather className="mx-auto h-6 w-6 text-tbc-gold" />
              <p className="mt-3 font-heading text-lg text-tbc-navy">
                Awaiting the first inscription
              </p>
              <p className="mt-1 font-body text-sm text-tbc-navy/60 max-w-md mx-auto">
                No businesses match your filter yet. The first entries will
                appear here as founders sign the charter.
              </p>
            </div>
          ) : (
            <div data-testid="founding-ledger-rows">
              {visible.map((entry) => (
                <LedgerRow key={entry.business_id} entry={entry} />
              ))}
            </div>
          )}

          {ledger.claimed > 0 && !ledger.closed && (
            <div className="text-center py-6 font-body text-[11px] tracking-[0.2em] text-tbc-navy/50 uppercase">
              ··· More founding businesses awaiting inscription ···
            </div>
          )}
        </div>
      </section>

      {/* ================ SIGN-THE-CHARTER STRIP ================ */}
      {!ledger.closed ? (
        <section className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 pb-14">
          <div className="rounded-2xl bg-tbc-navy p-6 sm:p-8 lg:px-10 flex flex-col md:flex-row items-center gap-5 md:gap-8 shadow-lg">
            <div className="flex-none h-16 w-16 rounded-full bg-tbc-gold/15 border border-tbc-gold/60 flex items-center justify-center">
              <Feather className="h-7 w-7 text-tbc-gold" />
            </div>
            <div className="flex-1 min-w-0 text-center md:text-left">
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-tbc-gold">
                Only {ledger.remaining} positions remain
              </div>
              <h3
                className="font-heading italic text-white mt-1 leading-tight"
                style={{ fontSize: "clamp(1.4rem, 3.4vw, 2rem)" }}
              >
                Sign the charter while it&apos;s still open.
              </h3>
              <p className="mt-2 font-body text-white/80 text-sm max-w-2xl md:max-w-none">
                Founding status is granted once and never reissued. Members
                receive lifetime founder pricing, a permanent place in the
                ledger, and first voice in shaping what TBC becomes.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/pricing#founding")}
              className="flex-none inline-flex items-center gap-2 rounded-md bg-tbc-gold px-6 py-3 text-tbc-navy font-body font-semibold hover:bg-tbc-gold/90 transition-colors shadow-md"
              data-testid="founding-strip-claim-cta"
            >
              Claim Your Entry <span aria-hidden>→</span>
            </button>
          </div>
        </section>
      ) : (
        <section className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 pb-14">
          <div
            className="rounded-2xl bg-tbc-navy p-8 text-center shadow-lg"
            data-testid="founding-charter-closed-panel"
          >
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-tbc-gold">
              The Founding 100
            </div>
            <h3 className="mt-2 font-heading italic text-white text-3xl sm:text-4xl">
              Charter Closed · 100 of 100
            </h3>
            <p className="mt-3 font-body text-white/80 text-sm max-w-xl mx-auto">
              The Founding Charter is complete. These 100 businesses hold a
              permanent place in The BLK Community&apos;s history.
            </p>
          </div>
        </section>
      )}

      {/* ================ FOOTER STRIP ================ */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 pb-8">
        <div className="flex flex-wrap items-center justify-center gap-3 text-[11px] uppercase tracking-[0.24em] font-semibold text-tbc-navy/60">
          <span>The BLK Community</span>
          <span className="text-tbc-gold">•</span>
          <span>Founding Charter</span>
          <span className="text-tbc-gold">•</span>
          <span>Established 2024</span>
        </div>
      </div>

      <Footer />
    </div>
  );
}
